import React from "react";
import Lottie from "react-lottie";

// Define types for component props
type EventListenerName = 
  "complete" | "loopComplete" | "enterFrame" | "segmentStart" | 
  "config_ready" | "data_ready" | "loaded_images" | "DOMLoaded" | "destroy";

interface EventListener {
  eventName: EventListenerName;
  callback: () => void;
}

interface LottieAnimationProps {
  animationData: any;
  width?: number | string;
  height?: number | string;
  loop?: boolean | number;
  autoplay?: boolean;
  isPaused?: boolean;
  isStopped?: boolean;
  speed?: number;
  segments?: readonly number[] | undefined;
  direction?: number;
  className?: string;
  style?: React.CSSProperties;
  eventListeners?: readonly EventListener[];
  ariaRole?: string | "button";
  ariaLabel?: string | "animation";
  isClickToPauseDisabled?: boolean;
  title?: string;
  rendererSettings?: {
    preserveAspectRatio?: string;
    context?: any;
    scaleMode?: any;
    clearCanvas?: boolean;
    progressiveLoad?: boolean;
    hideOnTransparent?: boolean;
    className?: string;
  };
}

/**
 * A reusable Lottie animation component that accepts customization props
 *
 * @param {LottieAnimationProps} props - The component props
 * @returns {JSX.Element} - The rendered Lottie animation
 */
const LottieAnimation: React.FC<LottieAnimationProps> = ({
  animationData,
  width = "100%",
  height = "100%",
  loop = false,
  autoplay = true,
  isPaused = false,
  isStopped = false,
  speed = 1,
  segments,
  direction = 1,
  className = "",
  style = {},
  eventListeners = [],
  ariaRole,
  ariaLabel,
  isClickToPauseDisabled,
  title,
  rendererSettings = {
    preserveAspectRatio: "xMidYMid slice",
  },
}) => {
  // Convert width/height to valid values for Lottie
  const getValidDimension = (value: number | string): number | string => {
    if (typeof value === "string" && value.endsWith("%")) {
      // Percentage values need special handling in Lottie
      return value;
    }
    // Convert to number for Lottie to handle correctly
    return typeof value === "number" ? value : parseInt(value, 10) || "100%";
  };

  const lottieWidth = getValidDimension(width);
  const lottieHeight = getValidDimension(height);

  // Prepare options for Lottie component
  const defaultOptions = {
    loop,
    autoplay,
    animationData,
    rendererSettings,
  };

  return (
    <div className={className} style={style}>
      <Lottie
        options={defaultOptions}
        height={lottieHeight}
        width={lottieWidth}
        isStopped={isStopped}
        isPaused={isPaused}
        speed={speed}
        segments={segments}
        direction={direction}
        eventListeners={eventListeners}
        ariaRole={ariaRole}
        ariaLabel={ariaLabel}
        isClickToPauseDisabled={isClickToPauseDisabled}
        title={title}
      />
    </div>
  );
};

export default LottieAnimation;